import {
    counterpartOf,
    IdentifiedRoi,
} from '~/domain/comparison/roiCounterpart';
import {
    ROI_TARGET_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';

export interface TypedRoi extends IdentifiedRoi {
    readonly interpretedType?: string;
}

// Targets are the plan's, never a segmentation model's: judged by type, not by name.
export function comparableRois(rois: readonly TypedRoi[]): TypedRoi[] {
    return rois.filter(
        (roi) =>
            !ROI_TARGET_TYPES.includes(
                (roi.interpretedType ?? '').toUpperCase() as RoiInterpretedType
            )
    );
}

interface RoiOverlapCandidate {
    readonly rois: readonly IdentifiedRoi[];
    // Runs several regions' networks together.
    readonly combined?: boolean;
}

// Every candidate, best first: most source structures paired, by name or suggestion alike;
// then a single region's model before a combined one; then the candidate offering fewer; then catalog order.
export function rankByOverlap<C extends RoiOverlapCandidate>(
    candidates: readonly C[],
    sourceRois: readonly TypedRoi[]
): { readonly candidate: C; readonly covered: number }[] {
    const wanted = comparableRois(sourceRois);
    return candidates
        .map((candidate, order) => ({
            candidate,
            order,
            // The same automatic pairing the ROI plan uses.
            covered: wanted.filter(
                (roi) => counterpartOf(roi, candidate.rois) !== undefined
            ).length,
        }))
        .sort(
            (a, b) =>
                b.covered - a.covered ||
                Number(!!a.candidate.combined) -
                    Number(!!b.candidate.combined) ||
                a.candidate.rois.length - b.candidate.rois.length ||
                a.order - b.order
        )
        .map(({ candidate, covered }) => ({ candidate, covered }));
}

// The top of a ranking, or none when no candidate pairs a single source structure.
export function bestRanked<C>(
    ranked: readonly { readonly candidate: C; readonly covered: number }[]
): C | undefined {
    const [first] = ranked;
    return first && first.covered > 0 ? first.candidate : undefined;
}
